import { ApplicationRef, ComponentRef, Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, createComponent, inject } from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/**
 * Directive that dynamically creates and manages an SvgIconComponent within a button element.
 *
 * Usage:
 * ```html
 * <button svgIcon="close" iconSize="md" iconClass="custom-class"></button>
 * ```
 */
@Directive({
  selector: 'button[svgIcon]',
  standalone: false,
})
export class SvgIconDirective implements OnInit, OnChanges, OnDestroy {
  private readonly el = inject(ElementRef);
  private readonly appRef = inject(ApplicationRef);

  @Input() svgIcon = '';
  @Input() iconSize: IconSize;
  @Input() iconClass: string;

  private iconCmpRef: ComponentRef<SvgIconComponent>;

  private get iconElement(): HTMLElement {
    return this.iconCmpRef.location.nativeElement as HTMLElement;
  }

  ngOnInit(): void {
    this.createIconComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.iconCmpRef) {
      return;
    }

    if (changes.iconClass) {
      this.updateIconClass(changes.iconClass.previousValue as string | undefined, changes.iconClass.currentValue as string | undefined);
    }

    if (changes.iconSize?.currentValue) {
      this.iconCmpRef.setInput('size', this.iconSize);
    }

    if (changes.svgIcon?.currentValue) {
      this.iconCmpRef.setInput('key', this.svgIcon);
    }
  }

  ngOnDestroy(): void {
    if (this.iconCmpRef) {
      this.appRef.detachView(this.iconCmpRef.hostView);
      this.iconCmpRef.destroy();
    }
  }

  private createIconComponent(): void {
    this.iconCmpRef = createComponent(SvgIconComponent, {
      environmentInjector: this.appRef.injector,
    });

    this.iconCmpRef.setInput('key', this.svgIcon);
    this.iconCmpRef.setInput('size', this.iconSize);

    if (this.iconClass) {
      this.iconElement.classList.add(this.iconClass);
    }

    this.appRef.attachView(this.iconCmpRef.hostView);
    (this.el.nativeElement as HTMLElement).appendChild(this.iconElement);
  }

  private updateIconClass(previousClass: string | undefined, currentClass: string | undefined): void {
    const classList = this.iconElement.classList;

    if (previousClass) {
      classList.remove(previousClass);
    }

    if (currentClass) {
      classList.add(currentClass);
    }
  }
}
